import * as mutation from "./mutation-types";
import { stateMerge } from "vue-object-merge";
import {
  getBlankRecord,
  isCurrentLevyCollection,
  getBlankInvoiceConfigurationRecord,
  getBlankEmailConfigurationRecord,
  getBlankLevyCalculationTimingConfigurationRecord,
  getBlankInvoicingTimingConfigurationRecord,
  getBlankFeesRecord,
  getBlankInformationSessionRecord,
  getBlankDataSourceConfigurationRecord,
  getBlankNonComplianceHistoryConfiguration,
  getBlankPublicLevyEstimatorConfiguration
} from "./helpers";
import { getInitialState, getUnloadedState } from "./state";
import store from "@/store";

export const mutations = {
  [mutation.STATE_RESET](state) {
    stateMerge(state, getInitialState());
  },
  [mutation.STATE_UNLOAD](state) {
    stateMerge(state, getUnloadedState());
  },
  [mutation.STATE_HIDE_ERROR](state, hideArgument) {
    stateMerge(state[hideArgument], { error: { show: false } });
  },

  [mutation.LIST_SET_PAGING](state, paging) {
    stateMerge(state.listing.paging, paging);
  },
  [mutation.LIST_STARTED](state) {
    state.listing.loading = true;
  },
  [mutation.LIST_FAILED](state) {
    state.listing.loading = false;
  },
  [mutation.LIST_SUCCEEDED](state, logs) {
    state.listing.loading = false;
    state.listing.loaded = true;
    state.listing.data = logs;
  },

  [mutation.VIEW_STARTED](state) {
    state.view.loading = true;
    state.view.loaded = false;
  },
  [mutation.VIEW_CLEAR](state) {
    state.view.loading = false;
    state.view.loaded = false;
    state.view.data = getBlankRecord();
  },
  [mutation.VIEW_FAILED](state) {
    state.view.loading = false;
    state.view.loaded = false;
    state.view.data = getBlankRecord();
  },
  [mutation.VIEW_SUCCEEDED](state, levyCollection) {
    state.view.loading = false;
    state.view.loaded = true;
    stateMerge(state.view.data, levyCollection);
    state.view.breadcrumbs = [];
    if (!isCurrentLevyCollection(levyCollection.id)) {
      state.view.breadcrumbs.push({
        text: "Levy collections",
        path: "/levy-collections"
      });
    }
    state.view.breadcrumbs.push({
      text: `${levyCollection.year} ${
        store.state.lookups.data.levyCollectionTypeDescriptions[levyCollection.type]
      } levy collection`
    });
  },

  [mutation.EDIT_SET_DATA](state, details) {
    stateMerge(state.edit.data, details);
  },

  [mutation.VIEW_INVOICE_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewInvoiceConfiguration.loading = true;
    state.viewInvoiceConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_INVOICE_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewInvoiceConfiguration.loading = false;
    state.viewInvoiceConfiguration.loaded = true;
    stateMerge(state.viewInvoiceConfiguration.data, configuration);
  },
  [mutation.VIEW_INVOICE_CONFIGURATION_FAILED](state) {
    state.viewInvoiceConfiguration.loading = false;
    state.viewInvoiceConfiguration.loaded = false;
  },

  [mutation.EDIT_INVOICE_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editInvoiceConfiguration.data, details);
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editInvoiceConfiguration.loading = true;
    state.editInvoiceConfiguration.error.show = false;
    state.editInvoiceConfiguration.levyCollectionId = levyCollectionId;
    state.editInvoiceConfiguration.data = getBlankInvoiceConfigurationRecord();
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editInvoiceConfiguration.loading = false;
    state.editInvoiceConfiguration.loaded = true;
    stateMerge(state.editInvoiceConfiguration.data, data);
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_GET_FAILED](state) {
    state.editInvoiceConfiguration.loading = false;
    state.editInvoiceConfiguration.loaded = false;
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_SAVE_STARTED](state) {
    state.editInvoiceConfiguration.saving = true;
    state.editInvoiceConfiguration.error.show = false;
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editInvoiceConfiguration.saving = false;
  },
  [mutation.EDIT_INVOICE_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editInvoiceConfiguration.saving = false;
    state.editInvoiceConfiguration.error.show = true;
    state.editInvoiceConfiguration.error.details = error;
  },

  [mutation.VIEW_INVOICING_TIMING_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewInvoicingTimingConfiguration.loading = true;
    state.viewInvoicingTimingConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_INVOICING_TIMING_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewInvoicingTimingConfiguration.loading = false;
    state.viewInvoicingTimingConfiguration.loaded = true;
    stateMerge(state.viewInvoicingTimingConfiguration.data, configuration);
  },
  [mutation.VIEW_INVOICING_TIMING_CONFIGURATION_FAILED](state) {
    state.viewInvoicingTimingConfiguration.loading = false;
    state.viewInvoicingTimingConfiguration.loaded = false;
  },

  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editInvoicingTimingConfiguration.data, details);
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editInvoicingTimingConfiguration.loading = true;
    state.editInvoicingTimingConfiguration.error.show = false;
    state.editInvoicingTimingConfiguration.levyCollectionId = levyCollectionId;
    state.editInvoicingTimingConfiguration.data = getBlankInvoicingTimingConfigurationRecord();
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editInvoicingTimingConfiguration.loading = false;
    state.editInvoicingTimingConfiguration.loaded = true;
    stateMerge(state.editInvoicingTimingConfiguration.data, data);
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_FAILED](state) {
    state.editInvoicingTimingConfiguration.loading = false;
    state.editInvoicingTimingConfiguration.loaded = false;
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_STARTED](state) {
    state.editInvoicingTimingConfiguration.saving = true;
    state.editInvoicingTimingConfiguration.error.show = false;
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editInvoicingTimingConfiguration.saving = false;
  },
  [mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editInvoicingTimingConfiguration.saving = false;
    state.editInvoicingTimingConfiguration.error.show = true;
    state.editInvoicingTimingConfiguration.error.details = error;
  },

  [mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewNonComplianceHistoryConfiguration.loading = true;
    state.viewNonComplianceHistoryConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewNonComplianceHistoryConfiguration.loading = false;
    state.viewNonComplianceHistoryConfiguration.loaded = true;
    stateMerge(state.viewNonComplianceHistoryConfiguration.data, configuration);
  },
  [mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_FAILED](state) {
    state.viewNonComplianceHistoryConfiguration.loading = false;
    state.viewNonComplianceHistoryConfiguration.loaded = false;
  },

  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editNonComplianceHistoryConfiguration.data, details);
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editNonComplianceHistoryConfiguration.loading = true;
    state.editNonComplianceHistoryConfiguration.error.show = false;
    state.editNonComplianceHistoryConfiguration.levyCollectionId = levyCollectionId;
    state.editNonComplianceHistoryConfiguration.data = getBlankNonComplianceHistoryConfiguration();
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editNonComplianceHistoryConfiguration.loading = false;
    state.editNonComplianceHistoryConfiguration.loaded = true;
    stateMerge(state.editNonComplianceHistoryConfiguration.data, data);
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_FAILED](state) {
    state.editNonComplianceHistoryConfiguration.loading = false;
    state.editNonComplianceHistoryConfiguration.loaded = false;
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_STARTED](state) {
    state.editNonComplianceHistoryConfiguration.saving = true;
    state.editNonComplianceHistoryConfiguration.error.show = false;
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editNonComplianceHistoryConfiguration.saving = false;
  },
  [mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editNonComplianceHistoryConfiguration.saving = false;
    state.editNonComplianceHistoryConfiguration.error.show = true;
    state.editNonComplianceHistoryConfiguration.error.details = error;
  },

  [mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewLevyCalculationTimingConfiguration.loading = true;
    state.viewLevyCalculationTimingConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewLevyCalculationTimingConfiguration.loading = false;
    state.viewLevyCalculationTimingConfiguration.loaded = true;
    stateMerge(state.viewLevyCalculationTimingConfiguration.data, configuration);
  },
  [mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_FAILED](state) {
    state.viewLevyCalculationTimingConfiguration.loading = false;
    state.viewLevyCalculationTimingConfiguration.loaded = false;
  },

  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editLevyCalculationTimingConfiguration.data, details);
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editLevyCalculationTimingConfiguration.loading = true;
    state.editLevyCalculationTimingConfiguration.error.show = false;
    state.editLevyCalculationTimingConfiguration.levyCollectionId = levyCollectionId;
    state.editLevyCalculationTimingConfiguration.data = getBlankLevyCalculationTimingConfigurationRecord();
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editLevyCalculationTimingConfiguration.loading = false;
    state.editLevyCalculationTimingConfiguration.loaded = true;
    stateMerge(state.editLevyCalculationTimingConfiguration.data, data);
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_FAILED](state) {
    state.editLevyCalculationTimingConfiguration.loading = false;
    state.editLevyCalculationTimingConfiguration.loaded = false;
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_STARTED](state) {
    state.editLevyCalculationTimingConfiguration.saving = true;
    state.editLevyCalculationTimingConfiguration.error.show = false;
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editLevyCalculationTimingConfiguration.saving = false;
  },
  [mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editLevyCalculationTimingConfiguration.saving = false;
    state.editLevyCalculationTimingConfiguration.error.show = true;
    state.editLevyCalculationTimingConfiguration.error.details = error;
  },

  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SET_DATA](state, data) {
    stateMerge(state.editPublicLevyEstimatorConfiguration.data, data);
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editPublicLevyEstimatorConfiguration.loading = true;
    state.editPublicLevyEstimatorConfiguration.error.show = false;
    state.editPublicLevyEstimatorConfiguration.levyCollectionId = levyCollectionId;
    state.editPublicLevyEstimatorConfiguration.data = getBlankPublicLevyEstimatorConfiguration();
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editPublicLevyEstimatorConfiguration.loading = false;
    stateMerge(state.editPublicLevyEstimatorConfiguration.data, data);
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_FAILED](state) {
    state.editPublicLevyEstimatorConfiguration.loading = false;
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_STARTED](state) {
    state.editPublicLevyEstimatorConfiguration.saving = true;
    state.editPublicLevyEstimatorConfiguration.error.show = false;
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editPublicLevyEstimatorConfiguration.saving = false;
  },
  [mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editPublicLevyEstimatorConfiguration.saving = false;
    state.editPublicLevyEstimatorConfiguration.error.show = true;
    state.editPublicLevyEstimatorConfiguration.error.details = error;
  },

  [mutation.VIEW_EMAIL_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewEmailConfiguration.loading = true;
    state.viewEmailConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_EMAIL_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewEmailConfiguration.loading = false;
    state.viewEmailConfiguration.loaded = true;
    stateMerge(state.viewEmailConfiguration.data, configuration);
  },
  [mutation.VIEW_EMAIL_CONFIGURATION_FAILED](state) {
    state.viewEmailConfiguration.loading = false;
    state.viewEmailConfiguration.loaded = false;
  },

  [mutation.EDIT_EMAIL_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editEmailConfiguration.data, details);
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editEmailConfiguration.loading = true;
    state.editEmailConfiguration.error.show = false;
    state.editEmailConfiguration.levyCollectionId = levyCollectionId;
    state.editEmailConfiguration.data = getBlankEmailConfigurationRecord();
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editEmailConfiguration.loading = false;
    state.editEmailConfiguration.loaded = true;
    stateMerge(state.editEmailConfiguration.data, data);
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_GET_FAILED](state) {
    state.editEmailConfiguration.loading = false;
    state.editEmailConfiguration.loaded = false;
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_SAVE_STARTED](state) {
    state.editEmailConfiguration.saving = true;
    state.editEmailConfiguration.error.show = false;
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editEmailConfiguration.saving = false;
  },
  [mutation.EDIT_EMAIL_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editEmailConfiguration.saving = false;
    state.editEmailConfiguration.error.show = true;
    state.editEmailConfiguration.error.details = error;
  },

  [mutation.VIEW_DATA_SOURCE_CONFIGURATION_STARTED](state, levyCollectionId) {
    state.viewDataSourceConfiguration.loading = true;
    state.viewDataSourceConfiguration.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_DATA_SOURCE_CONFIGURATION_SUCCEEDED](state, configuration) {
    state.viewDataSourceConfiguration.loading = false;
    state.viewDataSourceConfiguration.loaded = true;
    stateMerge(state.viewDataSourceConfiguration.data, configuration);
  },
  [mutation.VIEW_DATA_SOURCE_CONFIGURATION_FAILED](state) {
    state.viewDataSourceConfiguration.loading = false;
    state.viewDataSourceConfiguration.loaded = false;
  },

  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_SET_DATA](state, details) {
    stateMerge(state.editDataSourceConfiguration.data, details);
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_STARTED](state, levyCollectionId) {
    state.editDataSourceConfiguration.loading = true;
    state.editDataSourceConfiguration.error.show = false;
    state.editDataSourceConfiguration.levyCollectionId = levyCollectionId;
    state.editDataSourceConfiguration.data = getBlankDataSourceConfigurationRecord();
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_SUCCEEDED](state, data) {
    state.editDataSourceConfiguration.loading = false;
    state.editDataSourceConfiguration.loaded = true;
    stateMerge(state.editDataSourceConfiguration.data, data);
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_FAILED](state) {
    state.editDataSourceConfiguration.loading = false;
    state.editDataSourceConfiguration.loaded = false;
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_STARTED](state) {
    state.editDataSourceConfiguration.saving = true;
    state.editDataSourceConfiguration.error.show = false;
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_SUCCEEDED](state) {
    state.editDataSourceConfiguration.saving = false;
  },
  [mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_FAILED](state, error) {
    state.editDataSourceConfiguration.saving = false;
    state.editDataSourceConfiguration.error.show = true;
    state.editDataSourceConfiguration.error.details = error;
  },

  [mutation.VIEW_FEES_STARTED](state, levyCollectionId) {
    state.viewFees.loading = true;
    state.viewFees.levyCollectionId = levyCollectionId;
  },
  [mutation.VIEW_FEES_SUCCEEDED](state, configuration) {
    state.viewFees.loading = false;
    state.viewFees.loaded = true;
    stateMerge(state.viewFees.data, configuration);
  },
  [mutation.VIEW_FEES_FAILED](state) {
    state.viewFees.loading = false;
    state.viewFees.loaded = false;
  },

  [mutation.EDIT_FEES_SET_DATA](state, details) {
    stateMerge(state.editFees.data, details);
  },
  [mutation.EDIT_FEES_GET_STARTED](state) {
    state.editFees.loading = true;
    state.editFees.error.show = false;
    state.editFees.data = getBlankFeesRecord();
  },
  [mutation.EDIT_FEES_GET_FAILED](state) {
    state.editFees.loading = false;
  },
  [mutation.EDIT_FEES_GET_SUCCEEDED](state, levyCollection) {
    state.editFees.loading = false;
    stateMerge(state.editFees.data, levyCollection);
  },

  [mutation.EDIT_FEES_SAVE_STARTED](state) {
    state.editFees.saving = true;
    state.editFees.error.show = false;
  },
  [mutation.EDIT_FEES_SAVE_FAILED](state, error) {
    state.editFees.saving = false;
    state.editFees.error.show = true;
    state.editFees.error.details = error;
  },
  [mutation.EDIT_FEES_SAVE_SUCCEEDED](state) {
    state.editFees.saving = false;
  },

  [mutation.VIEW_INFORMATION_SESSIONS_STARTED](state, levyCollectionId) {
    state.viewInformationSessions.levyCollectionId = levyCollectionId;
    state.viewInformationSessions.loading = true;
  },
  [mutation.VIEW_INFORMATION_SESSIONS_FAILED](state) {
    state.viewInformationSessions.loading = false;
    state.viewInformationSessions.loaded = false;
  },
  [mutation.VIEW_INFORMATION_SESSIONS_SUCCEEDED](state, sessions) {
    state.viewInformationSessions.loading = false;
    state.viewInformationSessions.loaded = true;
    state.viewInformationSessions.data = sessions;
  },

  [mutation.EDIT_INFORMATION_SESSION_SET_DATA](state, details) {
    stateMerge(state.editInformationSession.data, details);
  },
  [mutation.EDIT_INFORMATION_SESSION_INITIALISE](state, levyCollectionId) {
    state.editInformationSession.levyCollectionId = levyCollectionId;
    state.editInformationSession.loading = false;
    state.editInformationSession.error.show = false;
    state.editInformationSession.data = getBlankInformationSessionRecord();
  },
  [mutation.EDIT_INFORMATION_SESSION_GET_STARTED](state, levyCollectionId) {
    state.editInformationSession.levyCollectionId = levyCollectionId;
    state.editInformationSession.loading = true;
    state.editInformationSession.error.show = false;
    state.editInformationSession.data = getBlankInformationSessionRecord();
  },
  [mutation.EDIT_INFORMATION_SESSION_GET_FAILED](state) {
    state.editInformationSession.loading = false;
  },
  [mutation.EDIT_INFORMATION_SESSION_GET_SUCCEEDED](state, informationSession) {
    state.editInformationSession.loading = false;
    stateMerge(state.editInformationSession.data, informationSession);
  },
  [mutation.EDIT_INFORMATION_SESSION_SAVE_STARTED](state) {
    state.editInformationSession.saving = true;
    state.editInformationSession.error.show = false;
  },
  [mutation.EDIT_INFORMATION_SESSION_SAVE_FAILED](state, error) {
    state.editInformationSession.saving = false;
    state.editInformationSession.error.show = true;
    state.editInformationSession.error.details = error;
  },
  [mutation.EDIT_INFORMATION_SESSION_SAVE_SUCCEEDED](state) {
    state.editInformationSession.saving = false;
  },
  [mutation.EDIT_INFORMATION_SESSION_DELETE_STARTED](state) {
    state.editInformationSession.saving = true;
    state.editInformationSession.error.show = false;
  },
  [mutation.EDIT_INFORMATION_SESSION_DELETE_FAILED](state, error) {
    state.editInformationSession.saving = false;
    state.editInformationSession.error.show = true;
    state.editInformationSession.error.details = error;
  },
  [mutation.EDIT_INFORMATION_SESSION_DELETE_SUCCEEDED](state) {
    state.editInformationSession.saving = false;
  },

  [mutation.GET_PROVIDER_TASKS_STARTED](state, levyCollectionId) {
    state.providerTasks.levyCollectionId = levyCollectionId;
    state.providerTasks.loading = true;
    state.providerTasks.loaded = false;
  },
  [mutation.GET_PROVIDER_TASKS_SUCCEEDED](state, tasks) {
    state.providerTasks.loading = false;
    state.providerTasks.loaded = true;
    state.providerTasks.data = tasks;
  },
  [mutation.GET_PROVIDER_TASKS_FAILED](state) {
    state.providerTasks.loading = false;
  },

  [mutation.TASKS_GET_STARTED](state, levyCollectionId) {
    state.tasks.levyCollectionId = levyCollectionId;
    state.tasks.loading = true;
    state.tasks.loaded = false;
  },
  [mutation.TASKS_GET_SUCCEEDED](state, tasks) {
    state.tasks.loading = false;
    state.tasks.loaded = true;
    state.tasks.data = tasks;
  },
  [mutation.TASKS_GET_FAILED](state) {
    state.tasks.loading = false;
  },

  [mutation.TASK_RUN_STARTED](state) {
    state.task.processing = true;
  },
  [mutation.TASK_RUN_SUCCEEDED](state) {
    state.task.processing = false;
  },
  [mutation.TASK_RUN_FAILED](state, error) {
    state.task.processing = false;
    state.task.error.show = true;
    state.task.error.details = error;
  },

  [mutation.CREATE_INITIALISE](state) {
    state.create.saving = false;
    state.create.type = null;
    state.create.data = getBlankRecord();
  },
  [mutation.CREATE_TYPE](state, type) {
    state.create.type = type;
  },
  [mutation.CREATE_STARTED](state) {
    state.create.saving = true;
  },
  [mutation.CREATE_FAILED](state) {
    state.create.saving = false;
  },
  [mutation.CREATE_SUCCEEDED](state, levyCollection) {
    state.create.data = levyCollection;
  },

  [mutation.GET_COMPONENT_LEVY_AMOUNTS_STARTED](state) {
    state.componentLevyAmounts.loaded = false;
    state.componentLevyAmounts.loading = true;
  },
  [mutation.GET_COMPONENT_LEVY_AMOUNTS_SUCCEEDED](state, chartData) {
    state.componentLevyAmounts.loaded = true;
    state.componentLevyAmounts.chartData = chartData;
    state.componentLevyAmounts.loading = false;
  },
  [mutation.GET_COMPONENT_LEVY_AMOUNTS_FAILED](state) {
    state.componentLevyAmounts.loading = false;
  },

  [mutation.INVOICE_TEMPLATE_UPLOAD_STARTED](state) {
    state.pdfTemplate.loading = true;
  },
  [mutation.INVOICE_TEMPLATE_UPLOAD_SUCCEEDED](state) {
    state.pdfTemplate.loading = false;
  },
  [mutation.INVOICE_TEMPLATE_UPLOAD_FAILED](state, error) {
    state.pdfTemplate.loading = false;
    state.pdfTemplate.error.show = true;
    state.pdfTemplate.error.details = error;
  },

  [mutation.FINISH_SETUP_STARTED](state) {
    state.setup.saving = true;
  },
  [mutation.FINISH_SETUP_SUCCEEDED](state) {
    state.setup.saving = false;
  },
  [mutation.FINISH_SETUP_FAILED](state) {
    state.setup.saving = false;
  },

  [mutation.GET_STATUS_HISTORY_STARTED](state, levyCollectionId) {
    state.statusHistory.levyCollectionId = levyCollectionId;
    state.statusHistory.loading = true;
  },
  [mutation.GET_STATUS_HISTORY_SUCCEEDED](state, history) {
    state.statusHistory.loading = false;
    state.statusHistory.loaded = true;
    state.statusHistory.data = history;
  },
  [mutation.GET_STATUS_HISTORY_FAILED](state) {
    state.statusHistory.loading = false;
    state.statusHistory.loaded = false;
  }
};
